import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  LayoutDashboard,
  TrendingUp,
  Receipt,
  ShoppingCart,
  FileStack,
  FileText,
  Building2,
  Warehouse,
  Package,
  List,
  Tag,
  Settings2,
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  Settings,
  CreditCard,
  Users,
  Store,
  PanelLeftClose,
  PanelLeft,
  BarChart3,
  Activity,
  Truck,
  Compass,
  type LucideIcon,
} from 'lucide-react';

import type { NavListProps } from 'src/components/nav-section/types';
import { TOUR_ANCHORS, navAnchor, useTour, APP_TOUR } from 'src/components/tour';
import { cn } from 'src/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from 'src/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import { useAuthContext } from 'src/auth/hooks';
import { useAppBootstrap } from 'src/contexts/app-bootstrap-context';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import RecommendationsNavBadge from 'src/sections/Recommendations/components/RecommendationsNavBadge';
import { useNavData } from './config-navigation';

// ----------------------------------------------------------------------

/**
 * Icon mapping from nav config titles to Lucide icons.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  Dashboard: LayoutDashboard,
  Forecasting: TrendingUp,
  Finances: Receipt,
  'Purchase Orders': ShoppingCart,
  Fulfillments: Truck,
  'Stock Changes': FileStack,
  'Stock Levels': Package,
  Products: FileText,
  Catalog: List,
  Analytics: BarChart3,
  Suppliers: Building2,
  Performance: Activity,
  Warehouses: Warehouse,
  Utilities: Package,
  Recommendations: Tag,
  Rules: Settings2,
};

// Live count badges per nav path, mirroring ICON_MAP's lookup idiom.
const NAV_BADGE_MAP: Record<string, React.ComponentType> = {
  [paths.recommendations.root]: RecommendationsNavBadge,
};

// ----------------------------------------------------------------------

interface NavItemData {
  title: string;
  path: string;
  icon?: LucideIcon;
  children?: NavItemData[];
  disabled?: boolean;
}

function isPathActive(pathname: string, itemPath: string): boolean {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function NavItem({ item }: { item: NavItemData }) {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const isActive = isPathActive(pathname, item.path);
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon || FileText;

  const isChildActive = item.children?.some((child) => isPathActive(pathname, child.path));

  if (hasChildren) {
    return (
      <Collapsible asChild defaultOpen={isActive || isChildActive} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={isCollapsed ? item.title : undefined}
              isActive={isActive || isChildActive}
            >
              <Icon className="h-4 w-4" />
              <span>{item.title}</span>
              <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children?.map((child) => {
                const ChildIcon = child.icon || FileText;
                return (
                  <SidebarMenuSubItem key={child.path}>
                    <SidebarMenuSubButton asChild isActive={isPathActive(pathname, child.path)}>
                      <Link to={child.path} data-tour={navAnchor(child.path)}>
                        <ChildIcon className="h-4 w-4" />
                        <span>{child.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  const NavBadge = NAV_BADGE_MAP[item.path];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={isCollapsed ? item.title : undefined} isActive={isActive}>
        <Link to={item.path} data-tour={navAnchor(item.path)}>
          <Icon className="h-4 w-4" />
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
      {NavBadge && <NavBadge />}
    </SidebarMenuItem>
  );
}

// ----------------------------------------------------------------------

function mapNavItem(item: NavListProps): NavItemData {
  return {
    title: item.title,
    path: item.path,
    icon: ICON_MAP[item.title],
    disabled: item.disabled,
    children: item.children?.map((child: NavListProps) => ({
      title: child.title,
      path: child.path,
      icon: ICON_MAP[child.title],
      disabled: child.disabled,
    })),
  };
}

// ----------------------------------------------------------------------

/**
 * Company Switcher - Header component for selecting company
 */
function CompanySwitcher() {
  const { state } = useSidebar();
  const { companies, selectedCompany, selectCompany } = useAppBootstrap();
  const router = useRouter();
  const isCollapsed = state === 'collapsed';
  const [isSwitching, setIsSwitching] = useState(false);

  const handleCompanySelect = async (companyId: string) => {
    const company = companies.find((c) => c.id === companyId);
    if (!company || company.id === selectedCompany?.id) return;

    try {
      setIsSwitching(true);
      await selectCompany(company, {
        onNavigationNeeded: (redirectPath: string) => {
          router.push(redirectPath);
        },
      });
    } catch (err) {
      console.error('Company switch error:', err);
    } finally {
      setIsSwitching(false);
    }
  };

  // Show logo only if single company or no companies
  if (!companies.length || companies.length === 1) {
    return (
      <Link to="/" className="flex items-center gap-2">
        <img
          src={
            isCollapsed
              ? '/assets/illustrations/Athory.svg'
              : '/assets/illustrations/Athory_with_text.svg'
          }
          alt="Athory"
          className={cn('transition-all duration-200', isCollapsed ? 'h-10 w-10' : 'h-14 w-14')}
        />
        {!isCollapsed && (
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-sidebar-foreground">
              {selectedCompany?.companyName || 'Athory'}
            </span>
          </div>
        )}
      </Link>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
                isSwitching && 'opacity-50'
              )}
              disabled={isSwitching}
            >
              <img
                src="/assets/illustrations/Athory.svg"
                alt="Athory"
                className="h-10 w-10 shrink-0"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left leading-tight">
                <span className="truncate text-sm font-semibold">
                  {selectedCompany?.companyName || 'Select Company'}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Companies
            </DropdownMenuLabel>
            {companies.map((company) => (
              <DropdownMenuItem
                key={company.id}
                onClick={() => handleCompanySelect(company.id)}
                className={cn('gap-2 p-2', company.id === selectedCompany?.id && 'bg-accent')}
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-sm border bg-background">
                  <Store className="h-4 w-4 shrink-0" />
                </div>
                <span className="truncate">{company.companyName}</span>
                {company.id === selectedCompany?.id && (
                  <span className="ml-auto text-xs text-primary">Current</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// ----------------------------------------------------------------------

/**
 * User Nav - Footer component for user account
 */
function UserNav() {
  const { state } = useSidebar();
  const { user, logout } = useAuthContext();
  const router = useRouter();
  const { startTour } = useTour();
  const isCollapsed = state === 'collapsed';

  const userName = `${user?.attributes?.given_name || ''} ${
    user?.attributes?.family_name || ''
  }`.trim();
  const userEmail = user?.attributes?.email || '';
  const userInitial = user?.attributes?.given_name?.charAt(0)?.toUpperCase() || 'U';

  const handleLogout = async () => {
    try {
      await logout();
      router.replace('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigate = (path: string) => {
    router.push(path);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              data-tour={TOUR_ANCHORS.userMenu}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                {user?.photoURL ? (
                  <img src={user.photoURL} alt={userName} className="h-8 w-8 rounded-lg" />
                ) : (
                  <span className="text-sm font-medium">{userInitial}</span>
                )}
              </div>
              {!isCollapsed && (
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left leading-tight">
                  <span className="truncate text-sm font-semibold">{userName || 'User'}</span>
                  <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                </div>
              )}
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
            align="end"
            side="top"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{userName}</p>
                <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => startTour(APP_TOUR.id)}>
                <Compass className="mr-2 h-4 w-4" />
                <span>Take the tour</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => handleNavigate(paths.settings.general)}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNavigate(paths.settings.billing)}>
                <CreditCard className="mr-2 h-4 w-4" />
                <span>Billing</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNavigate(paths.settings.users)}>
                <Users className="mr-2 h-4 w-4" />
                <span>Users</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNavigate(paths.settings.multiStore)}>
                <Store className="mr-2 h-4 w-4" />
                <span>Multi-Store</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// ----------------------------------------------------------------------

/**
 * Sidebar Collapse Toggle - Always visible button to expand/collapse sidebar
 */
function SidebarCollapseToggle() {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={toggleSidebar}
          tooltip={isCollapsed ? 'Expand sidebar' : undefined}
          className="text-muted-foreground hover:text-foreground"
        >
          {isCollapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4" />
              <span>Collapse</span>
            </>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// ----------------------------------------------------------------------

/**
 * AppSidebar - Main navigation sidebar using shadcn/ui components
 *
 * Features:
 * - Company switcher in header
 * - User account menu in footer
 * - Collapsible with icon-only mode (toggle button always visible)
 * - Tooltips when collapsed
 * - Nested navigation with collapsible submenus
 * - Active state highlighting
 * - Keyboard shortcut (Cmd+B / Ctrl+B) to toggle
 */
export function AppSidebar() {
  const navData = useNavData();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <CompanySwitcher />
      </SidebarHeader>

      <SidebarContent data-tour={TOUR_ANCHORS.sidebarNav}>
        {navData.map((section) => (
          <SidebarGroup key={section.subheader}>
            <SidebarGroupLabel>{section.subheader}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => (
                  <NavItem key={item.path} item={mapNavItem(item)} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarCollapseToggle />
        <Separator className="my-1" />
        <UserNav />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
